import { API_BASE_URL } from "../../config";
import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { clearPermissionNotice, clearPermissionRecovery, getPermissionNotice, restorePermissionSession } from "../Utils/permissionNotice";

const Home = () => {
    const navigate = useNavigate();
    const [isSidebarHidden, setIsSidebarHidden] = useState(false);
    const [userPermissions, setUserPermissions] = useState([]);
    const [permissionNotice, setPermissionNotice] = useState(() => getPermissionNotice());
    const [activeMode, setActiveMode] = useState(() => {
        const saved = localStorage.getItem("activeMode");
        return saved || "sales";
    });
    const [navSearch, setNavSearch] = useState("");
    const [collapsedNavGroups, setCollapsedNavGroups] = useState(() => new Set());

    const toggleNavGroup = (groupKey) => {
        setCollapsedNavGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupKey)) next.delete(groupKey);
            else next.add(groupKey);
            return next;
        });
    };

    const handleLogout = () => {
        localStorage.removeItem("user");
        clearPermissionRecovery();
        navigate("/login");
    };

    // Refresh permissions helper
    const refreshPermissions = async () => {
        restorePermissionSession();
        const stored = localStorage.getItem("user");
        if (!stored) {
            handleLogout();
            return;
        }
        const user = JSON.parse(stored);
        const token = user?.token;
        if (!token) {
            handleLogout();
            return;
        }
        try {
            const resp = await fetch(`${API_BASE_URL}/auth/api/main/user`, {
                method: "GET",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
            });
            if (resp.status === 401) {
                // Only an invalid/expired token ends the session.  A 403 means
                // the account is authenticated but lacks a permission.
                handleLogout();
                return;
            }
            if (resp.ok) {
                const userData = await resp.json();
                const updated = { ...user, permissions: userData.permissions || [] };
                localStorage.setItem("user", JSON.stringify(updated));
                setUserPermissions(updated.permissions);
                return;
            }
        } catch (e) {
            console.error('Permission refresh error', e);
        }
        // Fallback to existing localStorage permissions if request failed
        setUserPermissions(user.permissions || []);
    };

    // On mount
    useEffect(() => {
        refreshPermissions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onPermissionDenied = (event) => setPermissionNotice(event.detail || getPermissionNotice());
        window.addEventListener("icecys-permission-denied", onPermissionDenied);
        return () => window.removeEventListener("icecys-permission-denied", onPermissionDenied);
    }, []);

    // Persist active mode
    useEffect(() => {
        localStorage.setItem("activeMode", activeMode);
    }, [activeMode]);

    // On route change within Home
    const location = useLocation();
    useEffect(() => {
        refreshPermissions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const toggleSidebar = () => {
        setIsSidebarHidden((prevState) => !prevState);
    };

    // A menu is visible when the signed-in user has at least one of its
    // module permissions.  `user:read` was a legacy blanket UI gate and is
    // deliberately not present in V4 roles anymore.
    const hasPermission = (requiredPermissions) => {
        const required = Array.isArray(requiredPermissions)
            ? requiredPermissions
            : [requiredPermissions];

        return required.some((permission) => userPermissions.some((granted) => (
            granted === "superadmin:*"
            || granted === permission
            || granted === "*:read" && permission.endsWith(":read")
            || granted === `${permission.split(":")[0]}:admin`
            || (granted.endsWith(":*") && permission.startsWith(`${granted.slice(0, -1)}`))
        )));
    };

    // Top mode tabs (permission-based)
    const modeTabs = [
        {
            key: "sales",
            label: "ขาย",
            icon: "cash-register",
            permission: ["report:read", "report:write", "gps:read", "seller:read"]
        },
        {
            key: "factory",
            label: "ผลิต",
            icon: "industry",
            permission: ["hwcontrol:read", "hwcontrol:operate", "alert:read", "alert:write", "dds:read", "dds:manage", "scada:edit"]
        },
        {
            key: "dev",
            label: "Dev",
            icon: "code",
            permission: ["auth:admin", "superadmin:*"]
        },
    ];
    // Navigation items with their required permissions
    // Navigation items with their required permissions (and optional mode scoping)
    const navigationItems = [
        {
            path: "dashboard",
            label: "หน้าต่างแสดงผลรวม",
            permission: ["report:read", "report:write", "gps:read"],
            modes: ["sales"],
            group: "ภาพรวม/รายงาน",
            icon: "tachometer-alt"
        },
        {
            path: "DashMap",
            label: "แผนที่รวม",
            permission: "gps:read",
            modes: ["sales"],
            group: "ภาพรวม/รายงาน",
            icon: "map"
        },
        {
            path: "MapTrack",
            label: "ระบบตรวจสอบเส้นทาง",
            permission: "gps:read",
            modes: ["sales"],
            group: "ภาพรวม/รายงาน",
            icon: "route"
        },
        {
            path: "Develop_Feature",
            label: "ระบบนำทาง",
            permission: "superadmin:*",
            modes: ["dev"],
            icon: "directions"
        },
        {
            path: "SaleData-management",
            label: "ตรวจสอบยอดขาย",
            permission: ["report:read", "report:write"],
            modes: ["sales"],
            group: "ภาพรวม/รายงาน",
            icon: "search-dollar"
        },
        {
            path: "user-management",
            label: "จัดการผู้ใช้",
            // Gate on what this page's four endpoints actually accept, not on
            // auth:admin. /v4/users and /v4/catalog take iam:assign-role OR
            // seller:admin; tokens/view and tokens/same-or-lower take
            // auth:admin OR seller:admin. Accounting holds seller:admin +
            // role_sales:admin — i.e. it is allowed to issue driver QR tokens —
            // but held no auth:* at all, so the one page it needs for that was
            // hidden from it.
            permission: ["seller:admin", "iam:assign-role"],
            modes: ["factory"],
            group: "ผู้ใช้/ลูกค้า",
            icon: "users-cog"
        },
        {
            path: "salepriceAndItemManage",
            label: "จัดการสินค้าและราคาขาย",
            permission: "seller:write",
            modes: ["sales"],
            group: "ราคา/สินค้า",
            icon: "tag"
        },
        {
            path: "customerData",
            label: "จัดการผู้ชื้อ",
            permission: "customer:read",
            modes: ["factory"],
            group: "ผู้ใช้/ลูกค้า",
            icon: "address-book"
        },
        {
            path: "tank-management",
            label: "จัดการถัง",
            permission: "customer:read",
            modes: ["factory"],
            group: "ถัง",
            icon: "cube"
        },
        {
            path: "tank-requests",
            label: "คำขอถัง",
            permission: "customer:read",
            modes: ["factory"],
            group: "ถัง",
            icon: "clipboard-list"
        },
        {
            path: "bag-ledger",
            label: "บัญชีกระสอบรายวัน",
            permission: "report:write",
            modes: ["sales"],
            group: "ตัวแทน/บัญชี",
            icon: "book"
        },
        {
            path: "accounting-daily-report",
            label: "รายงานบัญชีรายวัน",
            permission: "report:write",
            modes: ["sales"],
            group: "ตัวแทน/บัญชี",
            icon: "file-invoice-dollar"
        },
        {
            path: "bag-credit",
            label: "เครดิตกระสอบ",
            permission: "report:write",
            modes: ["sales"],
            group: "ตัวแทน/บัญชี",
            icon: "box"
        },
        {
            path: "routine-customers",
            label: "ลูกค้าประจำสาย",
            permission: "customer:read",
            modes: ["sales"],
            group: "ลูกค้า",
            icon: "user-friends"
        },
        {
            path: "customer-purchase-behavior",
            label: "พฤติกรรมการซื้อลูกค้า",
            permission: "customer:read",
            modes: ["sales"],
            group: "ลูกค้า",
            icon: "chart-pie"
        },
        {
            path: "agent-management",
            label: "จัดการตัวแทน (Agent)",
            permission: "customer:read",
            modes: ["factory"],
            group: "ผู้ใช้/ลูกค้า",
            icon: "user-tie"
        },
        {
            path: "agent-sale-factory",
            label: "สร้างขายจากโรงงาน (Agent)",
            permission: "report:write",
            modes: ["sales"],
            group: "ตัวแทน/บัญชี",
            icon: "industry"
        },
        {
            path: "AlertSystem",
            label: "Alert System",
            permission: ["alert:read", "alert:write"],
            modes: ["factory"],
            group: "แจ้งเตือน",
            icon: "exclamation-triangle"
        },
        {
            path: "DeliveryMaster",
            label: "Delivery Master (Tracking)",
            permission: ["seller:read", "seller:write"],
            modes: ["sales"],
            group: "จัดส่ง",
            icon: "truck-loading"
        },
        {
            path: "DeliverySchedule",
            label: "Delivery Schedule (Active Times)",
            permission: ["seller:read", "seller:write"],
            modes: ["sales"],
            group: "จัดส่ง",
            icon: "calendar-alt"
        },
        {
            path: "driver-assignment-list",
            label: "มอบหมายคนขับ (Driver Assignments)",
            permission: "driverassignment:read",
            modes: ["sales"],
            group: "จัดส่ง",
            icon: "id-card"
        },
        {
            path: "LoadCellManager",
            label: "LoadCell Manager",
            permission: ["hwcontrol:read", "hwcontrol:operate"],
            modes: ["factory"],
            group: "Hardware/อุปกรณ์",
            icon: "weight"
        },
        {
            path: "HardWare",
            label: "HardWare",
            permission: ["hwcontrol:read", "hwcontrol:operate"],
            modes: ["factory"],
            group: "Hardware/อุปกรณ์",
            icon: "server"
        },
        {
            path: "Scada",
            label: "SCADA View",
            permission: "scada:edit",
            modes: ["factory"],
            group: "Hardware/อุปกรณ์",
            icon: "desktop"
        },
        {
            path: "ProfilePictureUpload",
            label: "ระบบทดสอบ [ทดสอบระบบอัพโหลดไฟล์]",
            permission: "superadmin:*",
            modes: ["dev"], // or any permission you prefer for testing features
            icon: "vial"
        },
        {
            path: "DdsAdmin",
            label: "DDS — จัดการจอแสดงผล",
            permission: "dds:manage",
            modes: ["factory"],
            group: "Hardware/อุปกรณ์",
            icon: "tv"
        },
        {
            path: "TabletTokenAdmin",
            label: "Tablet Token — แอปคนขับ/บัญชี",
            permission: "seller:admin",
            modes: ["factory"],
            group: "Token/อุปกรณ์เคลื่อนที่",
            icon: "tablet-alt"
        },
        {
            // ผู้ดูแลเท่านั้น (ใช้สิทธิ์เดียวกับหน้า IMD token) — สั่งเครื่อง IMD ผ่าน server เท่านั้น
            path: "ImdRemoteControl",
            label: "ควบคุมเครื่อง IMD",
            permission: "seller:admin",
            modes: ["factory"],
            group: "Token/อุปกรณ์เคลื่อนที่",
            icon: "tablet-alt"
        },
        {
            path: "ShiftAdmin",
            label: "จัดการกะ — ผูกรอบบรรจุ/เพิ่ม กับรอบขาย",
            permission: ["report:read", "report:write"],
            modes: ["dev"], // Vector 2026-09-26: "ไม่มีเมนู side bar ที่ dev"
            icon: "link"
        },
        {
            path: "PaymentCodeAdmin",
            label: "Payment Ref — เงินสด/ค่าใช้จ่าย/การชำระเงิน",
            permission: ["payment:read", "payment:write"],
            modes: ["sales"],
            group: "ตัวแทน/บัญชี",
            icon: "money-check-alt"
        },
        {
            path: "PermissionAdmin",
            label: "การกำหนดสิทธิ์ (Permission Management)",
            permission: ["iam:assign-role", "iam:edit-role", "superadmin:*"],
            modes: ["factory"],
            group: "ผู้ใช้/ลูกค้า",
            icon: "user-shield"
        },
        {
            path: "TaxInvoiceBulk",
            label: "ใบกำกับภาษี — สร้างเป็นชุด (Bulk)",
            permission: "taxinvoice:generate",
            modes: ["sales"],
            group: "ตัวแทน/บัญชี",
            icon: "file-invoice"
        },
        {
            path: "Cctv",
            label: "CCTV Live View (POC)",
            permission: "superadmin:*",
            modes: ["dev"],
            icon: "video"
        }
    ];

    // Group display order + icon per mode (sidebar section headers, phase 1 of the UI
    // cleanup). Dev mode intentionally has no groups — only 5 items, not worth splitting.
    const NAV_GROUPS_BY_MODE = {
        sales: [
            { key: "ภาพรวม/รายงาน", icon: "chart-line" },
            { key: "จัดส่ง", icon: "truck" },
            { key: "ราคา/สินค้า", icon: "tags" },
            { key: "ลูกค้า", icon: "users" },
            { key: "ตัวแทน/บัญชี", icon: "file-invoice-dollar" }
        ],
        factory: [
            { key: "ผู้ใช้/ลูกค้า", icon: "user-cog" },
            { key: "ถัง", icon: "gas-pump" },
            { key: "Hardware/อุปกรณ์", icon: "microchip" },
            { key: "Token/อุปกรณ์เคลื่อนที่", icon: "mobile-alt" },
            { key: "แจ้งเตือน", icon: "bell" }
        ]
    };

    const visibleNavigationItems = navigationItems.filter(item => hasPermission(item.permission));
    const visibleModeTabs = modeTabs.filter(tab => visibleNavigationItems.some(
        item => !Array.isArray(item.modes) || item.modes.includes(tab.key)
    ));
    const displayedMode = visibleModeTabs.some(tab => tab.key === activeMode)
        ? activeMode
        : visibleModeTabs[0]?.key;

    // Render-only helpers below — hasPermission/visibleNavigationItems/visibleModeTabs/
    // displayedMode above are unchanged from before this brief.
    const modeVisibleItems = visibleNavigationItems.filter(item => {
        if (Array.isArray(item.modes)) return item.modes.includes(displayedMode);
        return true;
    });
    const trimmedNavSearch = navSearch.trim().toLowerCase();
    const searchedNavItems = trimmedNavSearch
        ? modeVisibleItems.filter(item => item.label.toLowerCase().includes(trimmedNavSearch))
        : null;
    const navGroupsForMode = NAV_GROUPS_BY_MODE[displayedMode] || null;

    const renderNavItem = (item) => (
        <li key={item.path}>
            <NavLink
                to={item.path}
                className={({isActive}) => (isActive ? "active-tab" : "")}
            >
                {item.icon && <i className={`fas fa-${item.icon}`}></i>}
                <span>{item.label}</span>
            </NavLink>
        </li>
    );

    return (
        <div className="btc-home-container">
            {/* Restore button: only shown while sidebar is hidden, since it slides off-screen */}
            {isSidebarHidden && (
                <button
                    className="toggle-sidebar-button-fixed"
                    onClick={toggleSidebar}
                >
                    {">>"}
                </button>
            )}

            {/* Sidebar */}
            <div className={`btc-sidebar ${isSidebarHidden ? "hidden" : ""}`}>

                {/* Mode tabs moved to the top of the sidebar under the logo */}
                <div className="btc-topbar btc-topbar--in-sidebar">
                    {visibleModeTabs.map(tab => (
                        <button
                            key={tab.key}
                            className={`btc-topbar-tab ${displayedMode === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveMode(tab.key)}
                            title={tab.label}
                            aria-label={tab.label}
                        >
                            <i className={`fas fa-${tab.icon}`}></i>
                        </button>
                    ))}
                    <button
                        className="toggle-sidebar-button-inline"
                        onClick={toggleSidebar}
                    >
                        {"<<"}
                    </button>
                </div>


                <div className="logoHomeContainer">
                    <img src={`${process.env.PUBLIC_URL}/tk-bg3.png`} alt="Logo" className="logoHomeImage" />
                </div>



                <div className="btc-search-bar">
                    <input
                        type="text"
                        placeholder="ค้นหาเมนู..."
                        value={navSearch}
                        onChange={(e) => setNavSearch(e.target.value)}
                    />
                </div>

                {searchedNavItems ? (
                    searchedNavItems.length > 0 ? (
                        <ul>
                            {searchedNavItems.map(renderNavItem)}
                        </ul>
                    ) : (
                        <div className="btc-nav-empty">ไม่พบเมนูที่ค้นหา</div>
                    )
                ) : navGroupsForMode ? (
                    navGroupsForMode.map(group => {
                        const groupItems = modeVisibleItems.filter(item => item.group === group.key);
                        if (groupItems.length === 0) return null;
                        const isCollapsed = collapsedNavGroups.has(group.key);
                        return (
                            <div className="btc-nav-group" key={group.key}>
                                <div
                                    className="btc-nav-group-header"
                                    onClick={() => toggleNavGroup(group.key)}
                                >
                                    <i className={`fas fa-${group.icon}`}></i>
                                    <span>{group.key}</span>
                                    <i className={`fas fa-chevron-down btc-nav-group-chevron ${isCollapsed ? "collapsed" : ""}`}></i>
                                </div>
                                {!isCollapsed && (
                                    <ul>
                                        {groupItems.map(renderNavItem)}
                                    </ul>
                                )}
                            </div>
                        );
                    })
                ) : (
                    <ul>
                        {modeVisibleItems.map(renderNavItem)}
                    </ul>
                )}

                <button className="btc-logout-button" onClick={handleLogout}>Log Out</button>
            </div>

            {/* Content area */}
            <div className={`btc-content ${isSidebarHidden ? "expanded" : ""}`}>
                {permissionNotice && (
                    <div role="alert" style={{ margin: "12px", padding: "12px 16px", borderRadius: 6, background: "#fff3cd", color: "#664d03", border: "1px solid #ffecb5" }}>
                        <b>ไม่มีสิทธิ์ใช้งานหน้านี้</b>
                        {permissionNotice.permission
                            ? <> — ต้องมี permission <code>{permissionNotice.permission}</code></>
                            : <> — ระบบตอบกลับ 403 Forbidden</>}
                        <button type="button" onClick={() => { clearPermissionNotice(); setPermissionNotice(null); }} style={{ float: "right" }}>ปิด</button>
                    </div>
                )}
                <Outlet/>
            </div>
        </div>
    );
};

export default Home;
